import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { IconClose } from './icons';

// Drawer — Polish B2 mobile/a11y tranche. Generic slide-in side panel: focus trap (incl. Tab cycling), Escape
// closes, scrim click closes, body scroll lock, safe-area padding, role=dialog aria-modal, initial focus on the
// close button (always tabbable, so an empty-content panel never strands the trap), returns focus on close.
// Used as the CvWorkspace tailoring rail under 900px (was stacked below the whole CV). Ported from PSCraft v2's
// workspace/mobile/Drawer.tsx pattern into CVCraft's inline-style idiom.

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Drawer({
  open, onClose, label, children, testId = 'drawer', side = 'right', width,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
  testId?: string;
  side?: 'left' | 'right';
  // ADDITIVE (eval full-CV drawer): override the default min(85vw, 360px) panel width. The full-CV drawer is a
  // FULL-WIDTH sheet under 700px and a wider reading column above it — a CV needs more line than a tailoring rail.
  width?: string;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => { previouslyFocused.current?.focus?.(); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) { e.preventDefault(); last.focus(); }
      } else {
        if (active === last || !panel.contains(active)) { e.preventDefault(); first.focus(); }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const safeAreaStyle: CSSProperties = side === 'left'
    ? { paddingLeft: 'calc(env(safe-area-inset-left) + 0px)' }
    : { paddingRight: 'calc(env(safe-area-inset-right) + 0px)' };

  return (
    <div style={S.root} data-testid={`${testId}-root`}>
      <button
        type="button"
        aria-label={`Close ${label} panel by tapping outside`}
        tabIndex={-1}
        style={S.backdrop}
        data-testid={`${testId}-backdrop`}
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        data-testid={testId}
        style={{ ...(side === 'left' ? S.panelLeft : S.panelRight), ...safeAreaStyle, ...(width ? { width } : null) }}
      >
        <header style={S.header}>
          <h2 style={S.h2}>{label}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={`Close ${label}`}
            data-testid={`${testId}-close`}
            style={S.closeBtn}
          >
            <IconClose aria-hidden="true" />
          </button>
        </header>
        <div style={S.body}>{children}</div>
      </aside>
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  root: { position: 'fixed', inset: 0, zIndex: 40 },
  backdrop: { position: 'absolute', inset: 0, background: 'rgba(16,42,67,0.4)', border: 'none', cursor: 'default', padding: 0 },
  panelLeft: { position: 'absolute', top: 0, bottom: 0, left: 0, width: 'min(85vw, 360px)', background: 'var(--card)', borderRight: '1px solid var(--rule)', boxShadow: 'var(--elev-overlay)', overflowY: 'auto', display: 'flex', flexDirection: 'column', outline: 'none' },
  panelRight: { position: 'absolute', top: 0, bottom: 0, right: 0, width: 'min(85vw, 360px)', background: 'var(--card)', borderLeft: '1px solid var(--rule)', boxShadow: 'var(--elev-overlay)', overflowY: 'auto', display: 'flex', flexDirection: 'column', outline: 'none' },
  header: { position: 'sticky', top: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--rule)', background: 'var(--card)' },
  h2: { margin: 0, fontFamily: 'var(--serif)', fontSize: 'var(--fs-h3)', color: 'var(--ink)' },
  closeBtn: { minHeight: 44, minWidth: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 'var(--fs-h3)' },
  body: { flex: 1, padding: 'var(--space-3) var(--space-4)' },
};
